import { IconBook, IconCircleCheck, IconSparkles, IconX } from "@tabler/icons-react"
import { type FC, useEffect, useState } from "react"

import AiGenerationFeedback from "~/components/AiGenerationFeedback"
import { query } from "~/utilities/trpc"

import LoadingMessages from "./LoadingMessages"

type InProgressStatus = "queued" | "generating-outline" | "generating-content" | "generating-exam" | "finalizing"

const STAGE_CONFIG: Record<InProgressStatus, { start: number; end: number; label: string }> = {
	queued: { start: 4, end: 15, label: "Understanding your idea" },
	"generating-outline": { start: 18, end: 38, label: "Designing your course structure" },
	"generating-content": { start: 42, end: 92, label: "Generating your lessons" },
	"generating-exam": { start: 93, end: 98, label: "Creating your final exam" },
	finalizing: { start: 96, end: 99, label: "Finalizing your course" },
}

const AiGenerationStatus: FC<{ requestId: number }> = ({ requestId }) => {
	const { data, error } = query.ai.getCourseStatus.useQuery(requestId, {
		refetchInterval: query =>
			query.state.data !== null &&
			query.state.data?.status !== "completed" &&
			query.state.data?.status !== "error"
				? 3000
				: false,
	})

	const courseId = data?.status === "completed" ? data.courseId : null
	const { data: course } = query.course.get.useQuery(courseId!, { enabled: courseId !== null })

	const status = data?.status
	const isInProgress =
		status === "queued" ||
		status === "generating-outline" ||
		status === "generating-content" ||
		status === "generating-exam" ||
		status === "finalizing"

	const [progress, setProgress] = useState(0)

	useEffect(() => {
		if (!isInProgress) return
		const config = STAGE_CONFIG[status]
		setProgress(prev => Math.max(prev, config.start))

		const interval = setInterval(() => {
			setProgress(prev => (prev >= config.end ? prev : Math.min(config.end, prev + 0.25)))
		}, 400)
		return () => clearInterval(interval)
	}, [isInProgress, status])

	useEffect(() => {
		if (status === "completed") setProgress(100)
	}, [status])

	if (data === null && !error) {
		return (
			<div className="flex flex-col items-center justify-center py-20 text-center">
				<span className="text-sm text-gray-500">Request not found</span>
			</div>
		)
	}

	if (error || status === "error") {
		return (
			<div className="flex flex-col items-center gap-4 py-12 text-center">
				<div className="flex size-14 items-center justify-center rounded-full bg-red-50">
					<IconX className="size-7 text-red-600" />
				</div>
				<div className="flex max-w-md flex-col gap-1">
					<h3 className="text-lg font-semibold text-gray-900">Something went wrong</h3>
					<p className="text-sm text-gray-600">
						We couldn't generate your course. Please try again in a moment.
					</p>
				</div>
			</div>
		)
	}

	if (status === "completed") {
		return (
			<div className="flex flex-col items-center gap-6 py-10 text-center">
				<div className="relative">
					<div className="absolute inset-0 rounded-full bg-green-100 blur-xl" />
					<div className="relative flex size-20 items-center justify-center rounded-full bg-green-50 ring-8 ring-green-50/50">
						<IconCircleCheck className="size-12 text-green-600" strokeWidth={2} />
					</div>
				</div>
				<div className="flex max-w-md flex-col gap-2">
					<h3 className="text-2xl font-semibold text-gray-900">Your course is ready</h3>
					<p className="text-sm text-gray-600">
						Your AI-generated course is ready to review and publish whenever you are.
					</p>
				</div>
				{course && (
					<div className="flex w-full max-w-md items-center gap-3 rounded-xl border border-gray-200 bg-gray-50 p-4 text-left">
						<div className="bg-primary/10 flex size-10 shrink-0 items-center justify-center rounded-lg">
							<IconBook className="text-primary size-5" />
						</div>
						<div className="flex min-w-0 flex-1 flex-col">
							<span className="truncate text-base font-semibold text-gray-900">{course.title}</span>
							<span className="text-xs text-gray-500">Draft course</span>
						</div>
					</div>
				)}
				<AiGenerationFeedback id={requestId} type="course" />
			</div>
		)
	}

	if (!isInProgress) return null

	const config = STAGE_CONFIG[status]
	const displayProgress = Math.round(progress)

	return (
		<div className="flex flex-col items-center gap-7 py-4 text-center">
			<div className="relative">
				<div className="bg-primary/20 absolute inset-0 animate-ping rounded-full" />
				<div className="bg-primary/10 absolute inset-0 animate-pulse rounded-full" />
				<div className="from-primary/15 to-primary/5 ring-primary/5 relative flex size-20 items-center justify-center rounded-full bg-linear-to-br ring-8">
					<IconSparkles className="text-primary size-10" strokeWidth={1.75} />
				</div>
			</div>

			<div className="flex max-w-lg flex-col gap-2">
				<h3 className="text-2xl font-semibold text-gray-900">{config.label}</h3>
				<LoadingMessages status={status} className="min-h-5 text-base text-gray-600" />
				{status === "queued" && data?.status === "queued" && data.waitTimeMinutes > 0 && (
					<p className="mt-1 text-xs text-gray-500">
						Estimated wait: {data.waitTimeMinutes} {data.waitTimeMinutes === 1 ? "minute" : "minutes"}
					</p>
				)}
			</div>

			<div className="flex w-full max-w-md flex-col gap-2">
				<div className="h-1.5 w-full overflow-hidden rounded-full bg-gray-100">
					<div
						className="from-primary/70 to-primary h-full rounded-full bg-linear-to-r transition duration-700 ease-out"
						style={{ width: `${displayProgress}%` }}
					/>
				</div>
				<div className="flex items-center justify-between text-xs text-gray-500">
					<span className="font-medium tabular-nums">{displayProgress}%</span>
					<span>Generating in the background — safe to close this window</span>
				</div>
			</div>
		</div>
	)
}

export default AiGenerationStatus
